import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Button, Dropdown, Form, Toast } from "react-bootstrap";
import { createPortal } from "react-dom";
// import { ReactComponent as Calendar } from "assets/svg/outline-calendar.svg";
// hooks
import { useBlockedIntervals } from "../../../../contexts/BlockedIntervalsContext";
// services
import getDisconnectGoogle from "services/gcal/getDisconnectGoogle";
// utils
import { apiHostAndPath } from "utils/api";
import isGCalEvent from "utils/isGCalEvent";

// constants
import { GENERAL_ROUTES } from "constants/generalRoutes";
import { MESSAGE_GOOGLE_CALENDAR_FEATURE } from "constants/messageEventSources";
import messages from "messages";
// components
import LazyImage from "components/lazy-image";
import LogoLoadingIcon from "components/logo-loading-icon";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import RadioButton from "components/radio-button";
import { PUBLIC_ASSETS } from "constants/publicAssets";
import { useUser } from "contexts/UserContext";
import getGCalTwoWaySyncSettings from "services/gcal/getGCalTwoWaySyncSettings";
import putGCalTwoWaySyncSettings from "services/gcal/putGCalTwoWaySyncSettings";
import { AmplitudePageName, track } from "utils/amplitude";

import getClasses from "utils/getClasses";
import styles from "./GoogleCalendar.module.scss";
import getSelectedGCals from "services/gcal/getSelectedGCals";

const AUTH_URL = apiHostAndPath(
  "/api/scheduler_calendar_integrations/google_calendar_authorize_redirect",
);
const AUTOHIDE = 5000;

const ANALYTICS_COMPONENT_NAME = "connect-google-cal-modal";

type Props = {
  showConnectModal: boolean;
  setShowConnectModal: Dispatch<SetStateAction<boolean>>;
  connected: boolean | undefined;
  setConnected: Dispatch<SetStateAction<boolean | undefined>>;
  connectedAccounts: string[];
  analyticsPageName: AmplitudePageName;
  setGCals: Dispatch<SetStateAction<CalendarEntry[]>>;
  setShowSelectCalendarsModal: Dispatch<SetStateAction<boolean>>;
};

const NAME_DISPLAY_FORMATS = [
  {
    value: "first-initial-last-initial",
    label: "Initial (J.O.)",
  },
  {
    value: "first-initial-last-name",
    label: "First Initial, Last Name (J. Orchid)",
  },
  {
    value: "first-name-last-initial",
    label: "First Name, Last Initial (Jane O.)",
  },
  {
    value: "first-name-last-name",
    label: "First Name, Last Name (Jane Orchid)",
  },
];

/**
 * renders a button to sync or unsync Google Calendar
 */
const GoogleCalendar = ({
  showConnectModal,
  setShowConnectModal,
  connected,
  setConnected,
  connectedAccounts,
  analyticsPageName,
  setGCals,
  setShowSelectCalendarsModal,
}: Props) => {
  const { blockedIntervals, forceReload } = useBlockedIntervals();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  /**
   * when user is connected, refresh 15s till calendar events show up
   * @see https://github.com/orchiddevelopers/orchid-frontend/issues/1891
   */
  const [beginQuery, setBeginQuery] = useState(false);
  useEffect(() => {
    let timer: number;
    if (beginQuery && !blockedIntervals.some((el) => isGCalEvent(el))) {
      timer = window.setTimeout(() => {
        forceReload();
      }, 15000);
    } else {
      setBeginQuery(false);
    }
    return () => window.clearTimeout(timer);
  }, [beginQuery, blockedIntervals, forceReload]);

  useEffect(() => {
    const handler = (e) => {
      if (
        e.data &&
        typeof e.data === "object" &&
        (e.data || {}).source === MESSAGE_GOOGLE_CALENDAR_FEATURE &&
        (e.data.result || "").endsWith(GENERAL_ROUTES.googleCalendarSuccess)
      ) {
        // on success
        setBusy(false);
        setConnected(true);

        getSelectedGCals()
          .then((res) => {
            if (res.length === 0) {
              setShowSelectCalendarsModal(true);
              return;
            }
          })
          .catch(console.error);

        setMessage(messages.syncGoogleCalendarSuccess);
        setBeginQuery(true);
        setShowConnectModal(false);
      }

      if (
        e.data &&
        typeof e.data === "object" &&
        (e.data || {}).source === MESSAGE_GOOGLE_CALENDAR_FEATURE &&
        (e.data.result || "").endsWith(GENERAL_ROUTES.googleCalendarFailure)
      ) {
        // on failure
        setBusy(false);
        setMessage(messages.syncGoogleCalendarFailure);
      }
    };
    // should add event handler when it is on
    if (busy && !connected) {
      window.addEventListener("message", handler, false);
    } else {
      // remove event handler
      window.removeEventListener("message", handler, false);
    }
  }, [
    busy,
    connected,
    setConnected,
    setShowConnectModal,
    setShowSelectCalendarsModal,
  ]);

  const handleDisconnect = useCallback(async () => {
    setBusy(true);
    setMessage("");
    setShowConnectModal(false);

    try {
      await getDisconnectGoogle();
      setConnected(false);
      setBeginQuery(false);
      setMessage(messages.unsyncGoogleCalendarSuccess);
      // remove Google Calendar events immediately
      setGCals([]);
      forceReload();
    } catch (error) {
      setMessage(messages.unsyncGoogleCalendarFailure);
    } finally {
      setBusy(false);
    }
  }, [forceReload, setConnected, setShowConnectModal, setGCals]);

  const { user } = useUser();

  const [nameFormat, setNameFormat] = useState("first-initial-last-initial");
  const [enableTwoWaySync, setEnableTwoWaySync] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    if (!user?.has_calendar_google_connected) return;

    getGCalTwoWaySyncSettings()
      .then(({ name_format, enabled }) => {
        setNameFormat(name_format);
        setEnableTwoWaySync(enabled);
      })
      .catch((e) => {
        console.error(e);
      });
  }, [user]);

  const saveGCalTwoWaySyncSettings = async () => {
    try {
      await putGCalTwoWaySyncSettings({
        nameFormat,
        enabled: enableTwoWaySync,
      });
      setShowConnectModal(false);
    } catch (error) {
      setError(error);
    }
  };

  return (
    <>
      {/* toasts */}
      {message &&
        createPortal(
          <Toast
            onClose={() => setMessage("")}
            show={!!message}
            className={styles["google-calendar__message"]}
            delay={AUTOHIDE}
            autohide={true}
          >
            <Toast.Header>{message}</Toast.Header>
          </Toast>,
          document.body,
        )}

      <ModalLayout
        error={error}
        showHeaderDivider={true}
        show={showConnectModal}
        onHide={() => {
          track(
            analyticsPageName,
            [ANALYTICS_COMPONENT_NAME, "close-modal-button"],
            "click",
          );
          setShowConnectModal(false);
        }}
        Header={messages.syncWithGoogleCalendar}
        contentClassName={styles["connect-modal__content"]}
        headerClass={styles["connect-modal__header"]}
        bodyClass={styles["connect-modal__body"]}
        Footer={
          <>
            <Button
              variant="outline-primary"
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                  "click",
                );
                setShowConnectModal(false);
              }}
            >
              Cancel
            </Button>
            {connected && (
              <Button
                disabled={busy}
                tabIndex={0}
                size="sm"
                className={getClasses(
                  styles["google-calendar__button"],
                  styles["google-calendar__button--save"],
                )}
                onClick={() => {
                  track(
                    analyticsPageName,
                    [ANALYTICS_COMPONENT_NAME, "save-button"],
                    "click",
                  );
                  saveGCalTwoWaySyncSettings();
                }}
              >
                {messages.save}
              </Button>
            )}
          </>
        }
        footerClass={styles["connect-modal__footer"]}
      >
        {!user?.has_calendar_google_connected && (
          <>
            Sync your Google Calendar to stay up-to-date with appointments.
            Choose your preferred sync method to get started.
            <hr className={styles["connect-modal__hr"]} />
          </>
        )}
        <div className={styles["connect-modal__radio-section"]}>
          <RadioButton
            className={styles["connect-modal__radio"]}
            label={"One-Way Sync"}
            value={"one-way"}
            name="sync-option"
            onChange={() => setEnableTwoWaySync(false)}
            checked={!enableTwoWaySync}
          />
          {!enableTwoWaySync &&
            (!connected ? (
              <div className={styles["connect-modal__entry"]}>
                <div className={styles["connect-modal__entry-title"]}>
                  Google Calendar events appear in Orchid for viewing. This
                  secure option is recommended to maintain your privacy.
                </div>
                <div className="d-flex">
                  {busy ? (
                    <Button
                      variant="link"
                      size="sm"
                      disabled
                      className={styles["google-calendar__button"]}
                    >
                      <LogoLoadingIcon
                        className={styles["google-calendar__button-icon"]}
                      />
                      <span className={styles["google-calendar__button-text"]}>
                        {messages.loading}
                      </span>
                    </Button>
                  ) : (
                    <Button
                      tabIndex={0}
                      size="sm"
                      as="a"
                      href={AUTH_URL}
                      target="_blank"
                      // rel="opener" need for firefox
                      rel="opener"
                      className={styles["google-calendar__connect-button"]}
                      onClick={() => {
                        track(
                          analyticsPageName,
                          [
                            ANALYTICS_COMPONENT_NAME,
                            "sign-in-with-google-button",
                          ],
                          "click",
                        );
                        setBusy(true);
                      }}
                      // don't remove it, since prod will set unpassed disabled to true
                      // disabled={true}
                    >
                      <span
                        className={
                          styles["google-calendar__connect-button-icon"]
                        }
                      />
                      <span
                        className={
                          styles["google-calendar__connect-button-text"]
                        }
                      >
                        {messages.signInWithGoogle}
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles["connect-modal__entry"]}>
                {connectedAccounts.map((el, i) => (
                  <div
                    key={i}
                    className={styles["connect-modal__entry--connected"]}
                  >
                    <div className={styles["connect-modal__entry-left"]}>
                      <div className={styles["connect-modal__entry-icon"]}>
                        <LazyImage
                          src={PUBLIC_ASSETS.gcalIcon}
                          width={32}
                          height={"auto"}
                        />
                      </div>
                      <div
                        className={getClasses(
                          styles["connect-modal__entry-title--account"],
                        )}
                      >
                        {el}
                      </div>
                    </div>

                    <Button
                      tabIndex={0}
                      variant={"outline-primary"}
                      className={styles["connect-modal__entry-action"]}
                      onClick={() => {
                        track(
                          analyticsPageName,
                          [ANALYTICS_COMPONENT_NAME, "sign-out-button"],
                          "click",
                        );
                        handleDisconnect();
                      }}
                    >
                      Disconnect
                    </Button>
                  </div>
                ))}
              </div>
            ))}
        </div>
        <div className={styles["connect-modal__radio-section"]}>
          <RadioButton
            className={styles["connect-modal__radio"]}
            label={"Two-Way Sync"}
            value={"two-way"}
            name="sync-option"
            onChange={() => setEnableTwoWaySync(true)}
            checked={enableTwoWaySync}
          />
          {enableTwoWaySync && (
            <div className={styles["connect-modal__entry"]}>
              {!connected && (
                <div className={styles["connect-modal__entry-title"]}>
                  Allows updates between Orchid and Google Calendar. Your Orchid
                  appointments will sync to Google Calendar. By enabling this
                  two-way sync, you explicitly acknowledge and agree to adhere
                  to HIPAA regulations and to safeguard Orchid data within
                  Google Calendar.
                </div>
              )}

              <Form.Group className={styles["connect-modal__name-display"]}>
                <Form.Label className="rg-12 gray01 mb-1">
                  Name display format
                </Form.Label>
                <DropdownToggle
                  title={
                    NAME_DISPLAY_FORMATS.find((el) => el.value === nameFormat)
                      ?.label
                  }
                  Caret={<OutlineChevronDown />}
                >
                  {NAME_DISPLAY_FORMATS.map((el) => (
                    <Dropdown.Item
                      key={el.value}
                      onClick={() => setNameFormat(el.value)}
                    >
                      {el.label}
                    </Dropdown.Item>
                  ))}
                </DropdownToggle>
              </Form.Group>

              {!connected ? (
                <div className="d-flex">
                  {busy ? (
                    <Button
                      variant="link"
                      size="sm"
                      disabled
                      className={styles["google-calendar__button"]}
                    >
                      <LogoLoadingIcon
                        className={styles["google-calendar__button-icon"]}
                      />
                      <span className={styles["google-calendar__button-text"]}>
                        {messages.loading}
                      </span>
                    </Button>
                  ) : (
                    <Button
                      tabIndex={0}
                      size="sm"
                      as="a"
                      href={`${AUTH_URL}?two_way=true&name_format=${nameFormat}`}
                      target="_blank"
                      // rel="opener" need for firefox
                      rel="opener"
                      className={styles["google-calendar__connect-button"]}
                      onClick={() => {
                        track(
                          analyticsPageName,
                          [
                            ANALYTICS_COMPONENT_NAME,
                            "sign-in-with-google-button-two-way-sync",
                          ],
                          "click",
                        );
                        setBusy(true);
                      }}
                      // don't remove it, since prod will set unpassed disabled to true
                      // disabled={true}
                    >
                      <span
                        className={
                          styles["google-calendar__connect-button-icon"]
                        }
                      />
                      <span
                        className={
                          styles["google-calendar__connect-button-text"]
                        }
                      >
                        {messages.signInWithGoogle}
                      </span>
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <hr className={styles["connect-modal__hr"]} />
                  {connectedAccounts.map((el, i) => (
                    <div
                      key={i}
                      className={styles["connect-modal__entry--connected"]}
                    >
                      <div className={styles["connect-modal__entry-left"]}>
                        <div className={styles["connect-modal__entry-icon"]}>
                          <LazyImage
                            src={PUBLIC_ASSETS.gcalIcon}
                            width={32}
                            height={"auto"}
                          />
                        </div>
                        <div
                          className={getClasses(
                            styles["connect-modal__entry-title--account"],
                          )}
                        >
                          {el}
                        </div>
                      </div>
                      <Button
                        tabIndex={0}
                        variant={"outline-primary"}
                        className={styles["connect-modal__entry-action"]}
                        onClick={() => {
                          track(
                            analyticsPageName,
                            [ANALYTICS_COMPONENT_NAME, "sign-out-button"],
                            "click",
                          );
                          handleDisconnect();
                        }}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </ModalLayout>
    </>
  );
};

export default GoogleCalendar;
